/* GdprPanel — práva subjektu údajů přímo v kartě klienta (GDPR čl. 15–17, 20).
 *   • Souhlas se zpracováním (Ano/Ne) — zaznamená datum + zdroj
 *   • Indikátor, že je číslo účtu šifrované „at rest"
 *   • Stáhnout všechna data klienta (JSON export)
 *   • Smazat a zapomenout = ANONYMIZACE (osobní data pryč, smlouvy zůstanou anonymně)
 * Backend: /api/gdpr/contact/{id}/{summary|consent|export} + DELETE /api/gdpr/contact/{id}
 */
import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { ShieldCheck, Download, Trash2, Lock, Check, AlertTriangle } from "lucide-react";
import { RaynetSection } from "@/components/crm/RaynetUI";
import api from "@/lib/api";

export default function GdprPanel({ contact, onChanged }) {
  const id = contact?.id;
  const [sum, setSum] = useState(null);
  const [busy, setBusy] = useState("");

  const loadSummary = async () => {
    if (!id) return;
    try {
      const { data } = await api.get(`/api/gdpr/contact/${id}/summary`);
      setSum(data);
    } catch { /* nekritické */ }
  };
  useEffect(() => { loadSummary(); }, [id]); // eslint-disable-line

  const consent = sum?.consent_given ?? !!contact?.consent_given;

  const setConsent = async (given) => {
    setBusy("consent");
    try {
      await api.post(`/api/gdpr/contact/${id}/consent`, { given, source: "ručně v kartě klienta" });
      toast.success(given ? "Souhlas zaznamenán" : "Souhlas odvolán");
      await loadSummary();
      onChanged?.();
    } catch { toast.error("Nepovedlo se uložit souhlas"); }
    finally { setBusy(""); }
  };

  const exportData = async () => {
    setBusy("export");
    try {
      const res = await api.get(`/api/gdpr/contact/${id}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `gdpr_export_${(contact?.name || "klient").replace(/\s+/g, "_")}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Data klienta stažena");
    } catch { toast.error("Export se nepovedl"); }
    finally { setBusy(""); }
  };

  const forget = async () => {
    const nm = [contact?.name, contact?.surname].filter(Boolean).join(" ") || "klienta";
    if (!window.confirm(`Anonymizovat ${nm}?\n\nOsobní údaje (jméno, telefon, e-mail, číslo účtu, poznámky, přílohy) se NEVRATNĚ smažou. Smlouvy a obchody zůstanou anonymně kvůli zákonné archivaci.`)) return;
    if (!window.confirm("Opravdu? Tuto akci nelze vrátit zpět.")) return;
    setBusy("forget");
    try {
      const { data } = await api.delete(`/api/gdpr/contact/${id}`);
      const kept = Object.values(data?.summary?.kept || {}).reduce((a, b) => a + b, 0);
      toast.success(`Klient anonymizován${kept ? ` · ${kept} obchodních záznamů ponecháno` : ""}`);
      onChanged?.();
    } catch { toast.error("Anonymizace se nepovedla"); }
    finally { setBusy(""); }
  };

  if (contact?.anonymized) {
    return (
      <RaynetSection title="Soukromí & GDPR" icon={ShieldCheck}>
        <div className="flex items-center gap-2 text-[12px] p-2 rounded-lg" style={{ background: "var(--accent-soft)", color: "var(--ink)" }}>
          <Check size={14} style={{ color: "var(--positive, #5E8C5A)" }} />
          Tento klient byl <b>anonymizován</b>{contact?.anonymized_at ? ` (${String(contact.anonymized_at).slice(0, 10)})` : ""}. Osobní údaje byly odstraněny.
        </div>
      </RaynetSection>
    );
  }

  return (
    <RaynetSection title="Soukromí & GDPR" icon={ShieldCheck}>
      {/* Souhlas */}
      <div className="mb-2">
        <div className="text-popisek uppercase tracking-wide font-bold mb-1" style={{ color: "var(--muted)" }}>Souhlas se zpracováním</div>
        <div className="flex gap-1" data-testid="gdpr-consent">
          {[{ v: true, label: "Udělen" }, { v: false, label: "Neudělen" }].map((o) => {
            const active = consent === o.v;
            return (
              <button key={String(o.v)} disabled={busy === "consent"} onClick={() => setConsent(o.v)}
                className="flex-1 py-1.5 rounded-md text-drobny font-bold transition-colors disabled:opacity-50"
                style={{ background: active ? (o.v ? "var(--positive-text)" : "var(--surface)") : "var(--surface)",
                  /* Udělen: plocha tmavším odstínem zelené + text barvy plochy (≥ 4,5:1 ve všech motivech). */
                  color: active && o.v ? "var(--surface)" : active ? "var(--negative-text)" : "var(--muted)",
                  border: `1px solid ${active ? (o.v ? "var(--positive, #5E8C5A)" : "var(--cat-c, #BB5B43)") : "var(--line)"}` }}>
                {o.label}
              </button>
            );
          })}
        </div>
        {sum?.consent_at && consent && (
          <div className="text-drobny mt-1" style={{ color: "var(--faint)" }}>Zaznamenáno {String(sum.consent_at).slice(0, 10)}</div>
        )}
      </div>

      {/* Šifrování účtu */}
      <div className="flex items-center gap-2 text-drobny mb-2 px-2 py-1.5 rounded-md" style={{ background: "var(--surface)", border: "1px solid var(--line)", color: "var(--muted)" }}>
        <Lock size={12} style={{ color: sum?.bank_account_encrypted ? "var(--positive, #5E8C5A)" : "var(--faint)" }} />
        {sum?.bank_account_masked
          ? <>Číslo účtu <b style={{ color: "var(--ink)" }}>{sum.bank_account_masked}</b> — {sum.bank_account_encrypted ? "šifrováno" : "uloženo"}</>
          : <>Číslo účtu se ukládá šifrovaně (AES)</>}
      </div>

      {/* Akce */}
      <button onClick={exportData} disabled={busy === "export"} data-testid="gdpr-export"
        className="w-full flex items-center justify-center gap-1.5 py-1.5 mb-1.5 rounded-md text-drobny font-semibold transition-all hover:-translate-y-0.5 disabled:opacity-50"
        style={{ background: "var(--surface)", border: "1px solid var(--line)", color: "var(--ink)" }}>
        <Download size={13} /> {busy === "export" ? "Připravuji…" : "Stáhnout všechna data klienta"}
      </button>

      <button onClick={forget} disabled={busy === "forget"} data-testid="gdpr-forget"
        className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded-md text-drobny font-semibold transition-all hover:-translate-y-0.5 disabled:opacity-50"
        style={{ background: "var(--cat-c-soft, #F5D5C5)", border: "1px solid var(--cat-c, #BB5B43)", color: "var(--negative-text)" }}>
        <Trash2 size={13} /> {busy === "forget" ? "Anonymizuji…" : "Smazat a zapomenout"}
      </button>
      <div className="text-drobny mt-1 flex items-start gap-1" style={{ color: "var(--faint)" }}>
        <AlertTriangle size={10} className="mt-0.5 flex-shrink-0" />
        Anonymizace je nevratná. Smlouvy a obchody zůstanou anonymně kvůli zákonné archivaci.
      </div>
    </RaynetSection>
  );
}
